import { useQuery } from "@apollo/client";
import { useCurrentUser } from "hooks/useCurrentUser";
import { buildFeatureFlagMap, FeatureFlagOverrideContext } from "hooks/useFeatureFlags";
import { GET_TENANT_QUERY } from "hooks/useTenant";
import { type ReactNode, useMemo } from "react";

/**
 * Provides a specific tenant's feature flags to its subtree, overriding the ambient
 * current-tenant flags. The staff Manifest Review queue mounts customer manifest UIs
 * (drawer / discrepancy + document-review dialogs) for arbitrary tenants; without this
 * they'd resolve the staff user's session-tenant flags and hide the tenant-gated
 * sections (discrepancies, compliance, documents, EPA corrections).
 */
export function TenantFeatureFlagsProvider({
  tenantId,
  children,
}: {
  tenantId: string;
  children: ReactNode;
}) {
  const { isStaff } = useCurrentUser();
  const { data, loading } = useQuery(GET_TENANT_QUERY, {
    variables: { tenantId },
    skip: !tenantId,
  });
  const flags = useMemo(
    () => buildFeatureFlagMap(data?.tenant?.tenantConfigs, isStaff),
    [data?.tenant?.tenantConfigs, isStaff]
  );
  // Don't publish an override until the tenant's flags are loaded — otherwise the
  // subtree briefly sees all-default (off) flags and the flag-gated sections flash
  // hidden before popping in once the query resolves.
  if (loading) return null;
  return (
    <FeatureFlagOverrideContext.Provider value={flags}>
      {children}
    </FeatureFlagOverrideContext.Provider>
  );
}
